import React, { useMemo } from 'react'
import { BottomSheet, Separator, Typography } from '@s4l/ds'
import { type TaxDetail } from '../types'
import { getNotIncludedTaxCurrency } from '../utils'
import {
  filterTaxes,
  filterFees,
  getTaxesTotalsByPayment,
  getFeesTotalsByPayment,
  getAllTotalsByPayment,
  getTaxEffectiveAmount,
  getTaxEffectiveCurrency,
} from './utils'
import styles from './styles.module.css'
import { usePriceDetailsHelpers } from '../hooks'

interface TaxFeesDetailsProps {
  taxDetails: TaxDetail[]
  children: React.ReactNode
  isNewExperimentVariant?: boolean
}

export default function TaxFeesDetails({
  taxDetails,
  children,
  isNewExperimentVariant,
}: Readonly<TaxFeesDetailsProps>) {
  const { getTaxDisplayName, formatCurrency, formatMessage, formatMessageCapitalized } =
    usePriceDetailsHelpers()

  // Filtering operations
  const taxes = useMemo(() => filterTaxes(taxDetails), [taxDetails])
  const fees = useMemo(() => filterFees(taxDetails), [taxDetails])

  // Calculation operations
  const taxTotals = useMemo(() => getTaxesTotalsByPayment(taxDetails), [taxDetails])
  const feeTotals = useMemo(() => getFeesTotalsByPayment(taxDetails), [taxDetails])
  const allTotals = useMemo(() => getAllTotalsByPayment(taxDetails), [taxDetails])

  // Currency lookup
  const payAtPropertyCurrency = useMemo(() => getNotIncludedTaxCurrency(taxDetails), [taxDetails])

  return (
    <BottomSheet>
      <BottomSheet.Trigger asChild>{children}</BottomSheet.Trigger>
      <BottomSheet.Content
        title={formatMessage({ id: 'tax-breakdown.title' })}
        modalOnDesktop
        size="s"
      >
        <Typography variant="caption" size="l" color="var(--color-neutral-600)">
          {formatMessage({ id: 'tax-breakdown.body-1' })}
        </Typography>

        {taxes.length > 0 && (
          <div className={styles.section}>
            <div className={styles.detailsTitle}>
              <Typography variant="body" size="l" bold>
                {formatMessage({ id: 'tax-breakdown.taxes' })}
              </Typography>
              <Typography variant="body" size="l" bold>
                {formatCurrency(taxTotals.total, taxTotals.currency)}
              </Typography>
            </div>

            <Typography variant="caption" size="l" color="var(--color-neutral-600)">
              {formatMessage({ id: 'tax-breakdown.taxes-body' })}
            </Typography>

            <div className={styles.detailsBlock}>
              {taxes.map(tax => (
                <div className={styles.detailsRow} key={tax.name}>
                  <Typography variant="body" size="l">
                    {`${getTaxDisplayName(tax.name)} (${tax.included ? formatMessage({ id: 'tax-breakdown.pay-upon-booking' }) : formatMessage({ id: 'tax-breakdown.pay-at-hotel' })})`}
                  </Typography>
                  <Typography variant="body" size="l">
                    {formatCurrency(getTaxEffectiveAmount(tax), getTaxEffectiveCurrency(tax))}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}

        {fees.length > 0 && (
          <div className={styles.section}>
            <div className={styles.detailsTitle}>
              <Typography variant="body" size="l" bold>
                {formatMessage({ id: 'tax-breakdown.fees' })}
              </Typography>
              <Typography variant="body" size="l" bold>
                {formatCurrency(feeTotals.total, feeTotals.currency)}
              </Typography>
            </div>

            <Typography variant="caption" size="l" color="var(--color-neutral-600)">
              {formatMessage({ id: 'tax-breakdown.fees-body' })}
            </Typography>

            <div className={styles.detailsBlock}>
              {fees.map(fee => (
                <div className={styles.detailsRow} key={fee.name}>
                  <Typography variant="body" size="l">
                    {`${getTaxDisplayName(fee.name)} (${fee.included ? formatMessage({ id: 'tax-breakdown.pay-upon-booking' }) : formatMessage({ id: 'tax-breakdown.pay-at-hotel' })})`}
                  </Typography>
                  <Typography variant="body" size="l">
                    {formatCurrency(getTaxEffectiveAmount(fee), getTaxEffectiveCurrency(fee))}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator className={styles.separator} />

        <div className={styles.section}>
          <div className={styles.totalHeader}>
            {isNewExperimentVariant ? (
              <>
                <Typography variant="body" size="l" color="var(--color-neutral-800)" bold>
                  {formatMessage({ id: 'tax-breakdown.total' })}
                </Typography>
                <Typography variant="body" size="l" color="var(--color-neutral-800)" bold>
                  {formatCurrency(allTotals.total, allTotals.currency)}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="title" size="xs" bold>
                  {formatMessage({ id: 'tax-breakdown.total' })}
                </Typography>
                <Typography variant="title" size="xs" bold>
                  {formatCurrency(allTotals.total, allTotals.currency)}
                </Typography>
              </>
            )}
          </div>

          <div className={styles.detailsBlock} style={{ marginBlockEnd: 'var(--space-8)' }}>
            {allTotals.payNow > 0 && (
              <div className={styles.detailsRow}>
                <Typography variant="body" size="l" bold={isNewExperimentVariant}>
                  {formatMessageCapitalized({ id: 'tax-breakdown.pay-upon-booking' })}
                </Typography>
                <Typography variant="body" size="l" bold={isNewExperimentVariant}>
                  {formatCurrency(allTotals.payNow, allTotals.currency)}
                </Typography>
              </div>
            )}

            {allTotals.payLater > 0 && (
              <div className={styles.detailsRow}>
                <Typography variant="body" size="l" bold={isNewExperimentVariant}>
                  {formatMessageCapitalized({ id: 'tax-breakdown.pay-at-hotel' })}
                </Typography>
                <Typography variant="body" size="l" bold={isNewExperimentVariant}>
                  {formatCurrency(allTotals.payLater, allTotals.currency)}
                </Typography>
              </div>
            )}
          </div>

          {payAtPropertyCurrency && (
            <Typography variant="caption" size="l" color="var(--color-neutral-600)">
              {formatMessage(
                { id: 'tax-breakdown.payment-at-property' },
                { currency: payAtPropertyCurrency }
              )}
            </Typography>
          )}
        </div>
      </BottomSheet.Content>
    </BottomSheet>
  )
}
